/* Admin Controls Panel — every override action available in one place.
 *
 * Renders inside Account 360 (per-user controls) and Order 360 (per-order
 * controls). Each button opens a ReasonDialog enforcing the same
 * `reason` validation as the backend.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useScopes } from "../hooks/useScopes";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  AlertTriangle, KeyRound, ShieldOff, ShieldCheck, LogOut, UserCog,
  Mail, Fingerprint, UserCheck, ArrowDownToLine, ArrowUpRight, ArrowDownRight,
  Receipt, X, Lock, Unlock, Loader2, Eye, Pencil, Plus,
  Pause, Play, Link2Off, Ban, RotateCcw, Trash2, CreditCard, Users,
  Snowflake, CalendarClock, FileText, Sparkles, Power, Siren, Globe, Gauge, ArrowUpCircle,
} from "lucide-react";


/* ─── Quota meter (Phase B) ─────────────────────────────────────────────── */
export function QuotaMeter({ compact = false }) {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/admin/quotas/me").then((r) => {
      if (mounted) { setSnap(r.data); setLoading(false); }
    }).catch(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading || !snap) return null;
  if (snap.tier === "tier_unlimited") {
    return (
      <div className="rounded-lg border bg-emerald-50/50 border-emerald-200 px-3 py-2 flex items-center gap-2 text-[11px] text-emerald-800" data-testid="quota-meter">
        <Gauge className="w-3.5 h-3.5"/>
        <span><strong className="font-mono">{snap.tier_label}</strong> · no quota gate · all actions allowed</span>
      </div>
    );
  }
  const moneyPct = Math.min(100, snap.money_pct_used || 0);
  const countPct = Math.min(100, snap.count_pct_used || 0);
  const isCritical = moneyPct > 90 || countPct > 90;
  return (
    <div className={`rounded-lg border px-3 py-2 ${isCritical ? "bg-rose-50/50 border-rose-200" : "bg-sky-50/40 border-sky-200"}`} data-testid="quota-meter">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-[11px] font-medium">
          <Gauge className="w-3.5 h-3.5"/>
          Your tier: <span className="font-mono">{snap.tier_label}</span>
        </div>
        <a href="/admin/quotas" className="text-[10px] text-foreground/55 hover:text-foreground hover:underline">
          Manage →
        </a>
      </div>
      <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-2"} gap-2`}>
        <div>
          <div className="text-[9.5px] text-foreground/55">$ refunds today</div>
          <div className="text-[11px] font-mono tabular-nums">
            ${snap.usage.money_total_usd.toLocaleString()} / ${(snap.config.daily_refund_usd || 0).toLocaleString()}
          </div>
          <div className="h-1 w-full bg-foreground/10 rounded-full overflow-hidden mt-1">
            <div className={`h-full ${moneyPct > 80 ? "bg-rose-500" : "bg-emerald-500"}`}
                 style={{ width: `${moneyPct}%` }}/>
          </div>
        </div>
        <div>
          <div className="text-[9.5px] text-foreground/55">Actions today</div>
          <div className="text-[11px] font-mono tabular-nums">
            {snap.usage.action_count} / {snap.config.daily_action_count}
          </div>
          <div className="h-1 w-full bg-foreground/10 rounded-full overflow-hidden mt-1">
            <div className={`h-full ${countPct > 80 ? "bg-rose-500" : "bg-emerald-500"}`}
                 style={{ width: `${countPct}%` }}/>
          </div>
        </div>
      </div>
    </div>
  );
}



/* ─── Reason dialog (universal) ─────────────────────────────────────────── */
export function ReasonDialog({ open, onOpenChange, title, description, fields = [], submitLabel = "Apply", danger = false, onSubmit }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    if (!reason || reason.trim().length < 3) {
      toast.error("Reason is required (min 3 characters).");
      return;
    }
    for (const f of fields) {
      if (f.required && (form[f.key] === undefined || form[f.key] === "")) {
        toast.error(`${f.label} is required.`);
        return;
      }
    }
    setBusy(true);
    try {
      await onSubmit({ ...form, reason: reason.trim() });
      setReason(""); setForm({});
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message ||
                  e?.response?.data?.detail ||
                  e?.response?.data?.message ||
                  e?.message || "Failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setReason(""); setForm({}); } onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="reason-dialog" aria-describedby="reason-dialog-desc">
        <DialogHeader>
          <DialogTitle className={danger ? "text-rose-700" : ""}>{title}</DialogTitle>
          <DialogDescription id="reason-dialog-desc">
            {description || "Every override requires a reason (min 3 chars). It is audit-logged and surfaced to the affected user."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {fields.map(f => (
            <div key={f.key}>
              <Label htmlFor={f.key}>{f.label}{f.required && <span className="text-rose-600 ml-0.5">*</span>}</Label>
              {f.kind === "select" ? (
                <select
                  id={f.key}
                  className="w-full border border-input rounded-md h-9 px-3 text-sm bg-background"
                  value={form[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`reason-field-${f.key}`}
                >
                  <option value="">— select —</option>
                  {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <Input
                  id={f.key}
                  type={f.type || "text"}
                  placeholder={f.placeholder}
                  value={form[f.key] || ""}
                  onChange={(e) => set(f.key, f.type === "number" ? e.target.value : e.target.value)}
                  data-testid={`reason-field-${f.key}`}
                />
              )}
              {f.hint && <div className="text-[10.5px] text-foreground/55 mt-0.5">{f.hint}</div>}
            </div>
          ))}
          <div>
            <Label htmlFor="reason">Reason <span className="text-rose-600">*</span></Label>
            <Textarea
              id="reason"
              rows={3}
              placeholder="Why are you making this change? (audit-logged, shown to the user)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="reason-text"
            />
            <div className="text-[10.5px] text-foreground/55 mt-0.5">
              Min 3 characters. Recorded in the override audit log and surfaced to the affected user.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}><X className="w-4 h-4 mr-1"/>Cancel</Button>
          <Button onClick={handleSubmit} disabled={busy || reason.trim().length < 3} variant={danger ? "destructive" : "default"} data-testid="reason-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Action button card with icon ────────────────────────────────────── */
function ActionCard({ icon: Icon, label, hint, onClick, tone = "neutral", testid }) {
  const tones = {
    neutral: "border-border bg-background hover:bg-foreground/3",
    warn:    "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900",
    danger:  "border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-900",
    good:    "border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-xl border p-3 transition-colors w-full ${tones[tone]}`}
    >
      <div className="flex items-center gap-2 text-xs font-medium">
        <Icon className="w-3.5 h-3.5"/>
        <span>{label}</span>
      </div>
      {hint && <div className="mt-1 text-[10.5px] opacity-75">{hint}</div>}
    </button>
  );
}


/* ─── 🚨 Panic Button — atomic composite lockdown ───────────────────────
 * One-click fraud response. Suspends + kicks sessions + freezes every
 * card + deletes payment methods + revokes Allianz cover + pauses
 * storefront + disconnects every connection — in a single composite
 * audit row. Reversible via Unlockdown (unsuspend + unfreeze cards +
 * unpause storefront; payment methods + covers must be re-enrolled by
 * the user — this is intentional for compliance).
 */
function PanicButton({ user, onChange }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const lockedDown = !!user.locked_down || (user.suspended && (user.suspension_reason || "").startsWith("lockdown"));
  const isUnlock = lockedDown;

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason is required (min 3 chars).");
      return;
    }
    setBusy(true);
    try {
      const path = isUnlock
        ? `/admin/users/${user.id}/unlockdown`
        : `/admin/users/${user.id}/lockdown`;
      const r = await api.post(path, { reason: reason.trim() });
      const acts = r.data?.actions_taken || [];
      toast.success(isUnlock
        ? `Account unlocked · ${acts.length ? acts.join(", ") : "no-op"}`
        : `🚨 Locked down · ${acts.length} sub-actions executed`);
      setReason(""); setOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`rounded-xl border-2 px-4 py-3 ${isUnlock
          ? "border-amber-300 bg-amber-50"
          : "border-rose-400 bg-rose-50"}`}
        data-testid="panic-button-card"
      >
        <div className="flex items-center gap-3">
          <Siren className={`w-5 h-5 ${isUnlock ? "text-amber-700" : "text-rose-700"}`}/>
          <div className="flex-1 min-w-0">
            <div className={`text-[13px] font-semibold ${isUnlock ? "text-amber-900" : "text-rose-900"}`}>
              {isUnlock ? "Account is locked down" : "🚨 Panic button · composite lockdown"}
            </div>
            <div className={`text-[10.5px] mt-0.5 ${isUnlock ? "text-amber-800/80" : "text-rose-800/80"}`}>
              {isUnlock
                ? "Unsuspend + unfreeze cards + unpause storefront in one shot. PMs + covers must be re-enrolled by user."
                : "Suspend + kick sessions + freeze every card + delete PMs + revoke covers + pause storefront + disconnect connections."}
            </div>
          </div>
          <Button
            size="sm"
            variant={isUnlock ? "outline" : "destructive"}
            className={isUnlock ? "border-amber-400 text-amber-900 hover:bg-amber-100" : ""}
            data-testid={isUnlock ? "panic-unlockdown-btn" : "panic-lockdown-btn"}
            onClick={() => setOpen(true)}
          >
            <Siren className="w-3.5 h-3.5 mr-1"/>
            {isUnlock ? "Unlock" : "Lock down"}
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setReason(""); setOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="panic-dialog" aria-describedby="panic-desc">
          <DialogHeader>
            <DialogTitle className={isUnlock ? "text-amber-700" : "text-rose-700"}>
              {isUnlock ? "Confirm unlockdown" : "🚨 Confirm composite lockdown"}
            </DialogTitle>
            <DialogDescription id="panic-desc">
              {isUnlock
                ? "Reverses the lockdown: unsuspend + unfreeze cards + unpause storefront. PMs and Allianz covers are NOT auto-restored; the user must re-enrol them. Audit-logged."
                : "Executes 7 sub-actions atomically. The user will be immediately locked out and notified. Every sub-action is audit-logged and reversible."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="panic-reason">Reason <span className="text-rose-600">*</span></Label>
            <Textarea
              id="panic-reason"
              rows={3}
              placeholder={isUnlock ? "Why are you unlocking? (audit-logged)" : "Why are you locking down? (audit-logged, surfaced to user)"}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="panic-reason-text"
            />
            <div className="text-[10.5px] text-foreground/55 mt-1">
              Min 3 characters. Recorded in the override audit log.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant={isUnlock ? "default" : "destructive"}
              onClick={submit}
              disabled={busy || reason.trim().length < 3}
              data-testid="panic-submit"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
              {isUnlock ? "Confirm unlock" : "Lock down account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/* ─── Tenant invites panel (list + revoke + resend) ─────────────────── */
export function TenantInvitesPanel({ tenantId, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/tenants/${tenantId}/invites`);
      setRows(r.data.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tenantId]);

  if (!tenantId) return null;

  return (
    <div data-testid="ctl-invites-panel">
      <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">
        Tenant invites <span className="text-foreground/40">· {rows.length}</span>
      </h4>
      {loading ? (
        <div className="text-[11.5px] text-foreground/55 py-2 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin"/>Loading invites…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-[11.5px] text-foreground/55 py-2">No invites on this tenant.</div>
      ) : (
        <ul className="divide-y divide-border/40 rounded-xl border border-border bg-white">
          {rows.map((inv) => {
            const isPending = (inv.status || "pending") === "pending";
            return (
              <li key={inv.id} className="flex items-center gap-2 px-3 py-2 text-[12px]" data-testid={`ctl-invite-row-${inv.id}`}>
                <Mail className="w-3.5 h-3.5 text-foreground/55"/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{inv.email}</div>
                  <div className="text-[10.5px] mono text-foreground/55">
                    {inv.role || "member"} · {inv.status || "pending"}
                    {inv.expires_at && ` · exp ${new Date(inv.expires_at).toLocaleDateString()}`}
                    {inv.resend_count ? ` · resent ${inv.resend_count}x` : ""}
                  </div>
                </div>
                {isPending && (
                  <>
                    <Button size="sm" variant="outline" data-testid={`ctl-invite-resend-${inv.id}`}
                      onClick={() => setDialog({ kind: "resend", invite: inv })}>
                      <RotateCcw className="w-3 h-3 mr-1"/>Resend
                    </Button>
                    <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`ctl-invite-revoke-${inv.id}`}
                      onClick={() => setDialog({ kind: "revoke", invite: inv })}>
                      <Trash2 className="w-3 h-3 mr-1"/>Revoke
                    </Button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <ReasonDialog
        open={!!dialog}
        onOpenChange={(o) => !o && setDialog(null)}
        title={dialog?.kind === "resend" ? "Resend invite" : "Revoke invite"}
        danger={dialog?.kind === "revoke"}
        submitLabel={dialog?.kind === "resend" ? "Resend" : "Revoke"}
        onSubmit={async (body) => {
          if (dialog.kind === "resend") {
            await api.post(`/admin/invites/${dialog.invite.id}/resend`, { reason: body.reason });
            toast.success("Invite resent · audit-logged.");
          } else {
            await api.delete(`/admin/invites/${dialog.invite.id}`, { params: { reason: body.reason } });
            toast.success("Invite revoked · audit-logged.");
          }
          await load();
          onChange?.();
        }}
      />
    </div>
  );
}


/* ─── User-level admin controls ─────────────────────────────────────────── */
export function AdminUserControls({ data, onChange }) {
  const u = data.user;
  const [dialog, setDialog] = useState(null);  // {action, ...config}
  const isSuspended = !!u.suspended;
  const role = u.role;

  const close = () => setDialog(null);

  const post = async (path, payload) => {
    const r = await api.post(path, payload);
    toast.success("Action applied · audit-logged.");
    onChange?.();
    return r.data;
  };
  const patch = async (path, payload) => {
    const r = await api.patch(path, payload);
    toast.success("Updated · audit-logged.");
    onChange?.();
    return r.data;
  };

  return (
    <div className="space-y-4" data-testid="admin-user-controls">
      <QuotaMeter compact/>

      {isSuspended && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 flex items-center gap-2 text-xs text-rose-900" data-testid="suspended-banner">
          <Ban className="w-3.5 h-3.5"/>
          <div>
            <div className="font-semibold">Account suspended</div>
            <div className="text-[10.5px] opacity-80">
              {u.suspension_reason ? `Reason: ${u.suspension_reason}` : ""}
              {u.suspended_at && ` · since ${new Date(u.suspended_at).toLocaleString()}`}
            </div>
          </div>
        </div>
      )}

      {/* 🚨 PANIC BUTTON — atomic composite lockdown */}
      <PanicButton user={u} onChange={onChange}/>

      {/* Account lifecycle */}
      <div>
        <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Account lifecycle</h4>
        <div className="grid grid-cols-2 gap-2">
          {isSuspended ? (
            <ActionCard icon={Play} label="Unsuspend account" hint="Lift the platform-wide block" tone="good" testid="ctl-unsuspend"
              onClick={() => setDialog({ key: "unsuspend", title: "Unsuspend account", danger: false, submit: "Unsuspend",
                run: (body) => post(`/admin/users/${u.id}/unsuspend`, body) })}/>
          ) : (
            <ActionCard icon={Pause} label="Suspend account" hint="Block all writes platform-wide" tone="danger" testid="ctl-suspend"
              onClick={() => setDialog({ key: "suspend", title: "Suspend account", danger: true, submit: "Suspend",
                run: (body) => post(`/admin/users/${u.id}/suspend`, body) })}/>
          )}
          <ActionCard icon={KeyRound} label="Reset password" hint="Send a fresh reset link via email" testid="ctl-reset-pw"
            onClick={() => setDialog({ key: "reset-pw", title: "Reset password", submit: "Send reset link",
              run: (body) => post(`/admin/users/${u.id}/reset-password`, body) })}/>
          <ActionCard icon={Fingerprint} label="Reset 2FA" hint="Disable TOTP + SMS; user must re-enroll" testid="ctl-reset-2fa"
            onClick={() => setDialog({ key: "reset-2fa", title: "Reset 2FA", danger: true, submit: "Reset 2FA",
              run: (body) => post(`/admin/users/${u.id}/reset-2fa`, body) })}/>
          <ActionCard icon={LogOut} label="Kick all sessions" hint="Force re-login on every device" testid="ctl-kick"
            onClick={() => setDialog({ key: "kick", title: "Kick all sessions", submit: "Kick sessions",
              run: (body) => post(`/admin/users/${u.id}/kick-sessions`, body) })}/>
          <ActionCard icon={Mail} label="Force-verify email" hint="Mark email_verified=true" testid="ctl-verify-email"
            onClick={() => setDialog({ key: "verify-email", title: "Force-verify email", submit: "Mark verified",
              run: (body) => post(`/admin/users/${u.id}/force-verify-email`, body) })}/>
          <ActionCard icon={ShieldCheck} label="Force re-KYC" hint="Reset KYC status to pending" testid="ctl-re-kyc"
            onClick={() => setDialog({ key: "re-kyc", title: "Force re-KYC", danger: true, submit: "Force re-KYC",
              run: (body) => post(`/admin/users/${u.id}/force-re-kyc`, body) })}/>
          <ActionCard icon={Pencil} label="Edit profile fields" hint="Email, phone, company, EIN, business type" testid="ctl-edit-profile"
            onClick={() => setDialog({ key: "edit-profile", title: "Edit profile", submit: "Save",
              fields: [
                { key: "name",         label: "Name" },
                { key: "email",        label: "Email", type: "email" },
                { key: "phone",        label: "Phone" },
                { key: "company",      label: "Company" },
                { key: "ein",          label: "EIN" },
                { key: "business_type", label: "Business type (llc/corp/...)" },
              ],
              run: (body) => {
                const { reason, ...patch } = body;
                const cleaned = Object.fromEntries(Object.entries(patch).filter(([, v]) => v && v !== ""));
                return post(`/admin/users/${u.id}/edit-profile`, { patch: cleaned, reason });
              } })}/>
          <ActionCard icon={Eye} label="Impersonate (read-only)" hint="30-min token to view as this user" testid="ctl-impersonate"
            onClick={() => setDialog({ key: "impersonate", title: "Impersonate as this user", submit: "Generate token",
              fields: [
                { key: "mode", label: "Mode", kind: "select", required: true,
                  options: [{ value: "read", label: "Read-only (safe)" }, { value: "write", label: "Write (full)" }] },
                { key: "minutes", label: "Duration (minutes)", type: "number", placeholder: "30" },
              ],
              run: async (body) => {
                const { reason, mode, minutes } = body;
                const r = await api.post(`/admin/users/${u.id}/impersonate`, {
                  mode: mode || "read",
                  minutes: minutes ? Number(minutes) : 30,
                  reason,
                });
                // Save token to clipboard
                try { await navigator.clipboard.writeText(r.data.token); } catch {}
                toast.success(`Token issued · expires ${new Date(r.data.expires_at).toLocaleString()} · copied to clipboard`);
                return r.data;
              } })}/>
        </div>
      </div>

      {/* Money & wallet */}
      <div>
        <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Money & wallet</h4>
        <div className="grid grid-cols-2 gap-2">
          <ActionCard icon={ArrowUpRight} label="Credit wallet (+$)" hint="Goodwill / refund / write-in" tone="good" testid="ctl-credit"
            onClick={() => setDialog({ key: "credit", title: "Credit wallet", submit: "Credit",
              fields: [
                { key: "amount", label: "Amount (USD)", type: "number", required: true, placeholder: "100.00" },
                { key: "memo",   label: "Memo (optional)" },
              ],
              run: (body) => post(`/admin/users/${u.id}/wallet/credit`,
                { amount: Number(body.amount), reason: body.reason, memo: body.memo }) })}/>
          <ActionCard icon={ArrowDownRight} label="Debit wallet (−$)" hint="Correction / claw-back" tone="warn" testid="ctl-debit"
            onClick={() => setDialog({ key: "debit", title: "Debit wallet", danger: true, submit: "Debit",
              fields: [
                { key: "amount", label: "Amount (USD)", type: "number", required: true, placeholder: "100.00" },
                { key: "memo",   label: "Memo (optional)" },
              ],
              run: (body) => post(`/admin/users/${u.id}/wallet/debit`,
                { amount: Number(body.amount), reason: body.reason, memo: body.memo }) })}/>
          {role === "seller" && (
            <ActionCard icon={ArrowDownToLine} label="Force-payout" hint="Off-schedule ACH payout from wallet" testid="ctl-force-payout"
              onClick={() => setDialog({ key: "force-payout", title: "Force-payout seller", submit: "Payout",
                fields: [
                  { key: "amount", label: "Amount (USD)", type: "number", required: true, placeholder: "500.00" },
                  { key: "memo",   label: "Memo (optional)" },
                ],
                run: (body) => post(`/admin/users/${u.id}/force-payout`,
                  { amount: Number(body.amount), reason: body.reason, memo: body.memo }) })}/>
          )}
          {role === "buyer" && (
            <>
              <ActionCard icon={Receipt} label="Adjust credit limit" hint="Override Verqo Trade Score-derived limit" testid="ctl-credit-limit"
                onClick={() => setDialog({ key: "credit-limit", title: "Adjust credit limit", submit: "Save",
                  fields: [{ key: "limit", label: "New limit (USD)", type: "number", required: true, placeholder: "50000" }],
                  run: (body) => patch(`/admin/users/${u.id}/credit-limit`,
                    { limit: Number(body.limit), reason: body.reason }) })}/>
              <ActionCard icon={UserCog} label="Override Verqo Trade Score" hint="Time-boxed manual underwriter exception" testid="ctl-score"
                onClick={() => setDialog({ key: "score", title: "Override Verqo Trade Score", submit: "Save",
                  fields: [
                    { key: "score", label: "Score (300–850)", type: "number", required: true, placeholder: "750" },
                    { key: "days",  label: "Days (1–365)", type: "number", placeholder: "90" },
                  ],
                  run: (body) => patch(`/admin/users/${u.id}/score-override`,
                    { score: Number(body.score), days: Number(body.days || 90), reason: body.reason }) })}/>
            </>
          )}
        </div>
      </div>

      {/* Payout schedule (seller only) */}
      {role === "seller" && (
        <div>
          <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Payout schedule</h4>
          <div className="grid grid-cols-2 gap-2">
            <ActionCard icon={CalendarClock} label="Override payout schedule" hint="Frequency, minimum balance, destination" testid="ctl-payout-sched"
              onClick={() => setDialog({ key: "payout-sched", title: "Override payout schedule", submit: "Save",
                fields: [
                  { key: "enabled",   label: "Enabled", kind: "select", required: true,
                    options: [{ value: "true", label: "Enabled" }, { value: "false", label: "Disabled" }] },
                  { key: "frequency", label: "Frequency", kind: "select",
                    options: ["daily", "weekly", "monthly"].map(s => ({ value: s, label: s })) },
                  { key: "day_of_week",  label: "Day of week (0=Mon, 6=Sun)", type: "number" },
                  { key: "day_of_month", label: "Day of month (1–28)", type: "number" },
                  { key: "minimum_balance", label: "Minimum balance (USD)", type: "number" },
                ],
                run: (body) => {
                  const { reason } = body;
                  const out = {};
                  if (body.enabled !== undefined && body.enabled !== "") out.enabled = body.enabled === "true";
                  if (body.frequency) out.frequency = body.frequency;
                  if (body.day_of_week !== "" && body.day_of_week !== undefined) out.day_of_week = Number(body.day_of_week);
                  if (body.day_of_month !== "" && body.day_of_month !== undefined) out.day_of_month = Number(body.day_of_month);
                  if (body.minimum_balance !== "" && body.minimum_balance !== undefined) out.minimum_balance = Number(body.minimum_balance);
                  return patch(`/admin/users/${u.id}/payout-schedule`, { patch: out, reason });
                } })}/>
          </div>
        </div>
      )}

      {/* Cards (issued virtual cards) */}
      {(data.cards || []).length > 0 && (
        <div>
          <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Issued virtual cards</h4>
          <ul className="divide-y divide-border/40 rounded-xl border border-border bg-white">
            {(data.cards || []).map((c) => (
              <li key={c.id} className="flex items-center gap-2 px-3 py-2 text-[12px]" data-testid={`ctl-card-row-${c.id}`}>
                <CreditCard className="w-3.5 h-3.5 text-foreground/55"/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.label || `Card ····${c.last4 || ""}`}</div>
                  <div className="text-[10.5px] mono text-foreground/55">{c.brand || "card"} ····{c.last4 || "—"} · {c.status || "—"}</div>
                </div>
                {c.frozen || c.status === "frozen" ? (
                  <Button size="sm" variant="outline" data-testid={`ctl-card-unfreeze-${c.id}`}
                    onClick={() => setDialog({ key: "unfreeze-card", title: "Unfreeze card", submit: "Unfreeze",
                      run: (body) => post(`/admin/cards/${c.id}/unfreeze`, body) })}>
                    <Unlock className="w-3 h-3 mr-1"/>Unfreeze
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid={`ctl-card-freeze-${c.id}`}
                    onClick={() => setDialog({ key: "freeze-card", title: "Freeze card", danger: true, submit: "Freeze",
                      run: (body) => post(`/admin/cards/${c.id}/freeze`, body) })}>
                    <Snowflake className="w-3 h-3 mr-1"/>Freeze
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Payment methods · per-row delete */}
      {(data.payment_methods || []).length > 0 && (
        <div>
          <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Payment methods</h4>
          <ul className="divide-y divide-border/40 rounded-xl border border-border bg-white">
            {(data.payment_methods || []).map((pm) => (
              <li key={pm.id} className="flex items-center gap-2 px-3 py-2 text-[12px]" data-testid={`ctl-pm-row-${pm.id}`}>
                <CreditCard className="w-3.5 h-3.5 text-foreground/55"/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{pm.label || pm.bank_name || pm.brand || "Payment method"}</div>
                  <div className="text-[10.5px] mono text-foreground/55">····{pm.last4 || "—"} · {pm.type || pm.kind || "bank"}</div>
                </div>
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  data-testid={`ctl-pm-delete-${pm.id}`}
                  onClick={() => setDialog({ key: "delete-pm", title: "Delete payment method", danger: true, submit: "Delete",
                    run: async (body) => {
                      await api.delete(`/admin/payment-methods/${pm.id}`, { params: { reason: body.reason } });
                      toast.success("Payment method deleted · audit-logged.");
                      onChange?.();
                    } })}>
                  <Trash2 className="w-3 h-3 mr-1"/>Delete
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Team management */}
      <div>
        <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Team management</h4>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <ActionCard icon={Plus} label="Add team member" hint="Invite a teammate on user's behalf" tone="good" testid="ctl-team-add"
            onClick={() => setDialog({ key: "team-add", title: "Add team member", submit: "Add member",
              fields: [
                { key: "email", label: "Email", type: "email", required: true },
                { key: "name",  label: "Name (optional)" },
                { key: "role",  label: "Role", kind: "select", required: true,
                  options: ["admin", "operator", "viewer"].map(r => ({ value: r, label: r })) },
              ],
              run: (body) => post(`/admin/users/${u.id}/team`,
                { email: body.email, name: body.name, role: body.role, reason: body.reason }) })}/>
        </div>
        {(data.team_members || []).length > 0 && (
          <ul className="divide-y divide-border/40 rounded-xl border border-border bg-white">
            {(data.team_members || []).map(m => (
              <li key={m.id} className="flex items-center gap-2 px-3 py-2 text-[12px]" data-testid={`ctl-team-row-${m.id}`}>
                <Users className="w-3.5 h-3.5 text-foreground/55"/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{m.name || m.email}</div>
                  <div className="text-[10.5px] mono text-foreground/55">{m.email} · {m.role} · {m.status}</div>
                </div>
                {m.role !== "owner" && (
                  <>
                    <Button size="sm" variant="outline"
                      data-testid={`ctl-team-edit-role-${m.id}`}
                      onClick={() => setDialog({ key: "team-edit-role", title: `Change role · ${m.email}`, submit: "Save role",
                        fields: [
                          { key: "role", label: "New role", kind: "select", required: true,
                            options: ["admin", "operator", "viewer"]
                              .filter(r => r !== m.role)
                              .map(r => ({ value: r, label: r })) },
                        ],
                        run: (body) => patch(`/admin/team/${m.id}/role`, { role: body.role, reason: body.reason }) })}>
                      <Pencil className="w-3 h-3 mr-1"/>Role
                    </Button>
                    <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`ctl-team-remove-${m.id}`}
                      onClick={() => setDialog({ key: "team-remove", title: "Remove team member", danger: true, submit: "Remove",
                        run: async (body) => {
                          await api.delete(`/admin/team/${m.id}`, { params: { reason: body.reason } });
                          toast.success("Team member removed · audit-logged.");
                          onChange?.();
                        } })}>
                      <Trash2 className="w-3 h-3 mr-1"/>Remove
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tenant invites — sellers only */}
      {role === "seller" && data.tenant?.id && (
        <TenantInvitesPanel tenantId={data.tenant.id} onChange={onChange}/>
      )}

      {/* Custom domain — sellers only */}
      {role === "seller" && data.tenant?.id && (data.tenant?.settings?.custom_domain || data.tenant?.custom_domain) && (
        <div>
          <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Custom domain</h4>
          <div className="rounded-xl border border-border bg-white p-3 flex items-center gap-3" data-testid="ctl-domain-card">
            <Globe className="w-4 h-4 text-foreground/55"/>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium truncate mono">
                {data.tenant?.settings?.custom_domain || data.tenant?.custom_domain}
              </div>
              <div className="text-[10.5px] text-foreground/55">
                Status: {data.tenant?.settings?.custom_domain_status || "—"}
                {data.tenant?.settings?.custom_domain_provider && ` · ${data.tenant.settings.custom_domain_provider}`}
              </div>
            </div>
            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="ctl-domain-clear"
              onClick={() => setDialog({ key: "domain-clear", title: "Clear custom domain", danger: true, submit: "Clear domain",
                run: (body) => post(`/admin/tenants/${data.tenant.id}/domain/clear`, body) })}>
              <Trash2 className="w-3 h-3 mr-1"/>Clear
            </Button>
          </div>
        </div>
      )}

      {/* Buyer Hub data (buyer only) */}
      {role === "buyer" && ((data.external_invoices || []).length + (data.external_pos || []).length > 0) && (
        <div>
          <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Buyer Hub data</h4>
          {(data.external_pos || []).length > 0 && (
            <div className="mb-3">
              <div className="text-[10.5px] mono text-foreground/55 mb-1">External POs</div>
              <ul className="divide-y divide-border/40 rounded-xl border border-border bg-white">
                {(data.external_pos || []).slice(0, 10).map(p => (
                  <li key={p.id} className="flex items-center gap-2 px-3 py-2 text-[12px]" data-testid={`ctl-extpo-row-${p.id}`}>
                    <FileText className="w-3.5 h-3.5 text-foreground/55"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{p.supplier_name || p.vendor_name || "Supplier"}</div>
                      <div className="text-[10.5px] mono text-foreground/55">{p.po_number || (p.id || "").slice(0, 8)} · ${Number(p.total || p.amount || 0).toLocaleString()}</div>
                    </div>
                    <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`ctl-extpo-delete-${p.id}`}
                      onClick={() => setDialog({ key: "delete-extpo", title: "Delete external PO", danger: true, submit: "Delete",
                        run: async (body) => {
                          await api.delete(`/admin/buyer-hub/external-pos/${p.id}`, { params: { reason: body.reason } });
                          toast.success("External PO deleted · audit-logged.");
                          onChange?.();
                        } })}>
                      <Trash2 className="w-3 h-3 mr-1"/>Delete
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(data.external_invoices || []).length > 0 && (
            <div>
              <div className="text-[10.5px] mono text-foreground/55 mb-1">External invoices</div>
              <ul className="divide-y divide-border/40 rounded-xl border border-border bg-white">
                {(data.external_invoices || []).slice(0, 10).map(iv => (
                  <li key={iv.id} className="flex items-center gap-2 px-3 py-2 text-[12px]" data-testid={`ctl-extinv-row-${iv.id}`}>
                    <Receipt className="w-3.5 h-3.5 text-foreground/55"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{iv.supplier_name || "Supplier"}</div>
                      <div className="text-[10.5px] mono text-foreground/55">{iv.invoice_number || iv.code || (iv.id || "").slice(0, 8)} · ${Number(iv.amount || 0).toLocaleString()}</div>
                    </div>
                    <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`ctl-extinv-delete-${iv.id}`}
                      onClick={() => setDialog({ key: "delete-extinv", title: "Delete external invoice", danger: true, submit: "Delete",
                        run: async (body) => {
                          await api.delete(`/admin/buyer-hub/external-invoices/${iv.id}`, { params: { reason: body.reason } });
                          toast.success("External invoice deleted · audit-logged.");
                          onChange?.();
                        } })}>
                      <Trash2 className="w-3 h-3 mr-1"/>Delete
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <ReasonDialog
        open={!!dialog}
        onOpenChange={(o) => !o && close()}
        title={dialog?.title}
        fields={dialog?.fields || []}
        submitLabel={dialog?.submit || "Apply"}
        danger={dialog?.danger}
        onSubmit={async (body) => { await dialog.run(body); }}
      />
    </div>
  );
}


/* ─── Order-level admin controls ──────────────────────────────────────── */
export function AdminOrderControls({ order, onChange }) {
  const [dialog, setDialog] = useState(null);

  const post = async (path, payload) => {
    await api.post(path, payload);
    toast.success("Action applied · audit-logged.");
    onChange?.();
  };

  return (
    <div className="space-y-3" data-testid="admin-order-controls">
      <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65">Order overrides</h4>
      <div className="grid grid-cols-2 gap-2">
        <ActionCard icon={Ban} label="Force-cancel" hint="Cancel from any status" tone="danger" testid="ctl-order-cancel"
          onClick={() => setDialog({ title: "Force-cancel order", danger: true, submit: "Cancel order",
            run: (body) => post(`/admin/orders/${order.id}/force-cancel`, body) })}/>
        <ActionCard icon={UserCheck} label="Force status" hint="Set any status (pending / shipped / delivered)" testid="ctl-order-status"
          onClick={() => setDialog({ title: "Force order status", submit: "Set status",
            fields: [{ key: "status", label: "New status", kind: "select", required: true,
              options: ["pending", "processing", "transit", "delivered", "cancelled", "refund_pending", "refunded"]
                .map(s => ({ value: s, label: s })) }],
            run: (body) => post(`/admin/orders/${order.id}/force-status`, body) })}/>
        <ActionCard icon={Pencil} label="Edit order" hint="Amount, carrier, tracking, ready date" testid="ctl-order-edit"
          onClick={() => setDialog({ title: "Edit order fields", submit: "Save",
            fields: [
              { key: "total",   label: "Total (USD)", type: "number" },
              { key: "carrier", label: "Carrier" },
              { key: "tracking", label: "Tracking #" },
              { key: "buyer_po", label: "Buyer PO #" },
            ],
            run: (body) => {
              const { reason, ...rest } = body;
              const patch = Object.fromEntries(Object.entries(rest).filter(([, v]) => v && v !== ""));
              if (patch.total !== undefined) patch.total = Number(patch.total);
              return post(`/admin/orders/${order.id}/edit`, { patch, reason });
            } })}/>
        <ActionCard icon={ShieldCheck} label="Waive SLA breach" hint="Forgive lead-time miss" tone="good" testid="ctl-waive-sla"
          onClick={() => setDialog({ title: "Waive SLA breach", submit: "Waive",
            run: (body) => post(`/admin/orders/${order.id}/waive-sla`, body) })}/>
      </div>

      {/* Prebook intervention — only when order has a prebook */}
      {order.prebook && (
        <div className="pt-2">
          <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2">Prebook intervention</h4>
          <div className="grid grid-cols-2 gap-2">
            <ActionCard icon={UserCheck} label="Force-accept prebook" hint="On buyer's behalf" tone="good" testid="ctl-prebook-accept"
              onClick={() => setDialog({ title: "Force-accept prebook", submit: "Accept",
                run: (body) => post(`/admin/orders/${order.id}/prebook/accept`, body) })}/>
            <ActionCard icon={Ban} label="Decline prebook" hint="On buyer's behalf" tone="danger" testid="ctl-prebook-decline"
              onClick={() => setDialog({ title: "Decline prebook", danger: true, submit: "Decline",
                run: (body) => post(`/admin/orders/${order.id}/prebook/decline`, body) })}/>
            <ActionCard icon={X} label="Cancel prebook" hint="Tear-down deposit + refund flow" tone="danger" testid="ctl-prebook-cancel"
              onClick={() => setDialog({ title: "Cancel prebook", danger: true, submit: "Cancel",
                run: (body) => post(`/admin/orders/${order.id}/prebook/cancel`, body) })}/>
            <ActionCard icon={CalendarClock} label="Request extension" hint="Push ready-by date" testid="ctl-prebook-extend"
              onClick={() => setDialog({ title: "Request prebook extension", submit: "Extend",
                run: (body) => post(`/admin/orders/${order.id}/prebook/extend`, body) })}/>
          </div>
        </div>
      )}
      <ReasonDialog
        open={!!dialog}
        onOpenChange={(o) => !o && setDialog(null)}
        title={dialog?.title}
        fields={dialog?.fields || []}
        submitLabel={dialog?.submit || "Apply"}
        danger={dialog?.danger}
        onSubmit={async (body) => { await dialog.run(body); }}
      />
    </div>
  );
}


/* ─── Invoice-level admin controls ────────────────────────────────────── */
export function AdminInvoiceControls({ invoice, onChange }) {
  const [dialog, setDialog] = useState(null);
  const { hasScope } = useScopes();

  const post = async (path, payload) => {
    await api.post(path, payload);
    toast.success("Action applied · audit-logged.");
    onChange?.();
  };

  const isPaid = invoice.status === "paid";
  const isVoid = invoice.status === "void";
  const isRefunded = invoice.status === "refunded";

  const canForcePaid = hasScope("admin.invoice.force_paid");
  const canVoid      = hasScope("admin.invoice.void");
  const canRefund    = hasScope("admin.invoice.refund");

  // If the admin has no invoice-mutation scopes, render nothing so the
  // section header doesn't sit alone over an empty grid.
  if (!canForcePaid && !canVoid && !canRefund) {
    return (
      <div className="space-y-2" data-testid="admin-invoice-controls">
        <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65">Invoice overrides</h4>
        <div data-testid="ctl-inv-blocked" className="text-xs text-stone-500 italic">
          Your role does not permit invoice overrides. Ask an Engineering admin to elevate your scope.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="admin-invoice-controls">
      <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65">Invoice overrides</h4>
      <div className="grid grid-cols-2 gap-2">
        {!isPaid && !isVoid && canForcePaid && (
          <ActionCard icon={UserCheck} label="Force-paid" hint="Mark paid (off-platform settlement)" tone="good" testid="ctl-inv-paid"
            onClick={() => setDialog({ title: "Force-mark invoice paid", submit: "Mark paid",
              run: (body) => post(`/admin/invoices/${invoice.id}/force-paid`, body) })}/>
        )}
        {!isVoid && !isRefunded && canVoid && (
          <ActionCard icon={Ban} label="Void invoice" hint="Cancel the receivable" tone="warn" testid="ctl-inv-void"
            onClick={() => setDialog({ title: "Void invoice", danger: true, submit: "Void",
              run: (body) => post(`/admin/invoices/${invoice.id}/void`, body) })}/>
        )}
        {isPaid && !isRefunded && canRefund && (
          <ActionCard icon={RotateCcw} label="Refund" hint="Credit buyer wallet for full amount" tone="warn" testid="ctl-inv-refund"
            onClick={() => setDialog({ title: "Refund invoice", danger: true, submit: "Refund",
              run: (body) => post(`/admin/invoices/${invoice.id}/force-refund`, body) })}/>
        )}
      </div>
      <ReasonDialog
        open={!!dialog}
        onOpenChange={(o) => !o && setDialog(null)}
        title={dialog?.title}
        submitLabel={dialog?.submit || "Apply"}
        danger={dialog?.danger}
        onSubmit={async (body) => { await dialog.run(body); }}
      />
    </div>
  );
}
